import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { ChevronDown, Search } from "./icons.js";

export interface RequirementOption {
  id: string;
  title: string;
  /** Small second line, e.g. "active · 12 candidates". */
  meta?: string;
}

/**
 * Searchable single-select for picking a requirement (Candidates + Interviews tabs). A button
 * opens a filter box over a listbox; keyboard: ↑ ↓ Enter Esc. `allOption`, when given, is pinned
 * first and hidden while the user is typing a search.
 */
export function RequirementCombobox({
  options,
  value,
  onSelect,
  allOption,
  loading = false,
  className,
}: {
  options: RequirementOption[];
  value: string;
  onSelect: (id: string) => void;
  allOption?: RequirementOption;
  loading?: boolean;
  className?: string;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = (allOption && allOption.id === value ? allOption : options.find((o) => o.id === value)) ?? allOption;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q) return options.filter((o) => o.title.toLowerCase().includes(q));
    return allOption ? [allOption, ...options] : options;
  }, [options, query, allOption]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    inputRef.current?.focus();
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const choose = (id: string): void => {
    onSelect(id);
    setOpen(false);
    setQuery("");
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = filtered[active];
      if (pick) choose(pick.id);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className={`req-combo${className ? ` ${className}` : ""}`} ref={rootRef}>
      <button
        type="button"
        className="req-combo-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Requirement"
        disabled={loading}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="req-combo-value">{loading ? "Loading…" : (selected?.title ?? "Select a requirement")}</span>
        <ChevronDown className="req-combo-chevron" />
      </button>

      {open && (
        <div className="req-combo-pop">
          <div className="req-combo-search">
            <Search className="req-combo-search-icon" />
            <input
              ref={inputRef}
              role="combobox"
              aria-expanded="true"
              aria-controls="req-combo-listbox"
              aria-activedescendant={filtered[active] ? `req-opt-${filtered[active].id}` : undefined}
              placeholder="Search requirements"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKey}
            />
          </div>
          <ul id="req-combo-listbox" role="listbox" className="req-combo-list" ref={listRef}>
            {filtered.length === 0 && <li className="req-combo-none">No requirement matches “{query}”</li>}
            {filtered.map((o, idx) => (
              <li
                key={o.id}
                id={`req-opt-${o.id}`}
                data-idx={idx}
                role="option"
                aria-selected={o.id === value}
                className={`req-combo-opt${idx === active ? " active" : ""}${o.id === value ? " selected" : ""}`}
                onMouseEnter={() => setActive(idx)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(o.id)}
              >
                <span className="req-opt-title">{o.title}</span>
                {o.meta && <span className="req-opt-meta">{o.meta}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
